/**
 * Product-form custom variants block.
 * Active only when [data-custom-variants-block] exists on the page.
 */
(function () {
  if (!document.querySelector("[data-custom-variants-block]")) return;

  const SELECTORS = {
    block: "[data-custom-variants-block]",
    item: "[data-form-custom-variant-item]",
    addonInput: '[data-custom-variants-block] input[name="custom_variant_addon_id"]',
    sticky: "[data-custom-variants-sticky]",
    stickySelect: "[data-custom-variants-sticky-select]",
    priceList: "[data-product-price-list]",
    mainBiscuitsPrice:
      "product-meta [data-product-price-list] .price:not(.price--compare)[data-biscuits-price]",
    gallery: "product-media flickity-carousel",
  };

  const PRICE_TARGETS = [
    {
      current:
        "product-meta [data-product-price-list] .price:not(.price--compare) .money",
      compare:
        "product-meta [data-product-price-list] .price--compare .money",
      currentWrapper:
        "product-meta [data-product-price-list] .price:not(.price--compare)",
      compareWrapper: "product-meta [data-product-price-list] .price--compare",
    },
    {
      current: "product-sticky .price-list .price:not(.price--compare) .money",
      compare: "product-sticky .price-list .price--compare .money",
      currentWrapper: "product-sticky .price-list .price:not(.price--compare)",
      compareWrapper: "product-sticky .price-list .price--compare",
    },
  ];

  function formatMoney(cents) {
    const value = cents / 100;
    const currency = window.themeVariables?.settings?.currencyCode ?? "";
    const formattedPrice = value.toFixed(2).replace(".", ",");
    return currency === "CHF"
      ? `${currency} ${formattedPrice}`
      : `${formattedPrice} ${currency}`;
  }

  function getCombinedPrices(mainPrices, addonPrices, isAddonActive) {
    if (!isAddonActive) {
      return { current: mainPrices.current, compare: mainPrices.compare };
    }

    const current = mainPrices.current + addonPrices.current;
    let compare = 0;
    const mainHasCompare = mainPrices.compare > mainPrices.current;
    const addonHasCompare = addonPrices.compare > addonPrices.current;

    if (mainHasCompare) {
      compare = addonHasCompare
        ? mainPrices.compare + addonPrices.compare
        : mainPrices.compare + addonPrices.current;
    } else if (addonHasCompare) {
      compare = mainPrices.current + addonPrices.compare;
    }

    return { current, compare };
  }

  function getExternalPriceMutationFlags(mutations) {
    let shouldSyncMainPrices = false;
    let shouldRefreshPrices = false;
    let shouldSyncSticky = false;

    for (const mutation of mutations) {
      for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        if (
          node.matches?.(SELECTORS.priceList) ||
          node.querySelector?.(SELECTORS.priceList)
        ) {
          shouldSyncMainPrices = true;
          shouldRefreshPrices = true;
        }

        if (
          node.nodeName === "PRODUCT-STICKY" ||
          node.querySelector?.("product-sticky")
        ) {
          shouldRefreshPrices = true;
          shouldSyncSticky = true;
        }
      }
    }

    return { shouldSyncMainPrices, shouldRefreshPrices, shouldSyncSticky };
  }

  function enableFormSubmit(formEl) {
    Array.from(formEl.elements)
      .filter(
        (el) =>
          (el instanceof HTMLInputElement || el instanceof HTMLButtonElement) &&
          el.type === "submit"
      )
      .forEach((button) => {
        button.removeAttribute("disabled");
        button.removeAttribute("aria-busy");
      });
  }

  function showCartError(formEl, error) {
    formEl.dispatchEvent(
      new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: { status: "error", error },
      })
    );
  }

  async function queryCart() {
    if (typeof window.Cart?.query === "function") {
      return window.Cart.query();
    }

    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: "same-origin",
    });

    return response.json();
  }

  function appendNamedFormFields(target, source, prefix) {
    for (const [key, value] of source.entries()) {
      if (!key.startsWith(prefix) || value == null || String(value) === "") continue;
      target.append(key, value);
    }
  }

  /**
   * Cart attributes from the product form (e.g. Versandhinweis).
   * cart/change.js cannot set them — used after qty-merge via cart/update.js.
   * @param {FormData | null | undefined} source
   * @returns {Record<string, string>}
   */
  function collectCartAttributes(source) {
    const attributes = {};
    if (!source) return attributes;
    for (const [key, value] of source.entries()) {
      const match = /^attributes\[(.+)\]$/.exec(key);
      if (!match || value == null || String(value) === "") continue;
      attributes[match[1]] = String(value);
    }
    return attributes;
  }

  async function syncCartAttributes(sourceFormData) {
    const attributes = collectCartAttributes(sourceFormData);
    if (Object.keys(attributes).length === 0) return;

    const cartUrl = window.themeVariables?.routes?.cartUrl || "/cart";
    try {
      await fetch(`${cartUrl}/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ attributes }),
      });
    } catch (_e) {
      // Line items already added; keep ATC working if attribute sync fails.
    }
  }

  async function mergeOrAddCartItem(variantId, quantity, sourceFormData) {
    const numericVariantId = Number(variantId);
    const cart = await queryCart();
    const existingLine = cart.items.find(
      (item) => item.variant_id === numericVariantId
    );

    if (existingLine) {
      return fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: existingLine.key,
          quantity: existingLine.quantity + quantity,
        }),
      });
    }

    const formData = new FormData();
    formData.append("id", String(numericVariantId));
    formData.append("quantity", String(quantity));
    if (sourceFormData) {
      appendNamedFormFields(formData, sourceFormData, "properties[");
      appendNamedFormFields(formData, sourceFormData, "attributes[");
    }

    return fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    });
  }

  class CustomVariantsBlock {
    constructor() {
      this.mainPrices = { current: 0, compare: 0 };
      this._isUpdatingPrices = false;
      this._priceRefreshFrame = null;
      this.onClick = this.onClick.bind(this);
      this.onStickyChange = this.onStickyChange.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      document.addEventListener("click", this.onClick, true);
      document.addEventListener("change", this.onStickyChange);
      document.addEventListener("Variant:Changed", this.onVariantChanged);
      this.setupPriceObservers();
      this.init();
    }

    isAddonActive() {
      return this.getActiveItem()?.dataset.isMainProduct === "false";
    }

    schedulePriceRefresh() {
      if (!this.isAddonActive()) return;

      if (this._priceRefreshFrame) {
        cancelAnimationFrame(this._priceRefreshFrame);
      }

      this._priceRefreshFrame = requestAnimationFrame(() => {
        this._priceRefreshFrame = null;
        this.refreshDisplayedPrices();
      });
    }

    init() {
      this.syncMainPricesFromFreshDom();
      const activeItem = this.getActiveItem();
      if (activeItem) {
        this.applySelection(activeItem, { syncSticky: true, updateGallery: false });
      }
    }

    setupPriceObservers() {
      const observeTarget =
        document.querySelector("product-meta")?.closest(".shopify-section") ||
        document.querySelector("product-meta");

      if (!observeTarget) return;

      this.priceObserver = new MutationObserver((mutations) => {
        if (this._isUpdatingPrices) return;

        const flags = getExternalPriceMutationFlags(mutations);
        if (!flags.shouldRefreshPrices) return;

        if (flags.shouldSyncMainPrices) {
          this.syncMainPricesFromFreshDom();
        }

        if (this.isAddonActive()) {
          this.schedulePriceRefresh();
        }

        if (flags.shouldSyncSticky) {
          this.syncStickyToActiveItem({ silent: true });
        }
      });

      this.priceObserver.observe(observeTarget, {
        childList: true,
        subtree: true,
      });
    }

    getMainBlock() {
      return document.querySelector(SELECTORS.block);
    }

    queryItems(selector = SELECTORS.item) {
      const block = this.getMainBlock();
      return block ? block.querySelectorAll(selector) : [];
    }

    getAddonItem() {
      const block = this.getMainBlock();
      if (!block) return null;

      return block.querySelector(
        `${SELECTORS.item}[data-is-main-product="false"]`
      );
    }

    getActiveItem() {
      const block = this.getMainBlock();
      if (!block) return null;

      return block.querySelector(`${SELECTORS.item}.active`);
    }

    getItemByIndex(itemIndex) {
      const block = this.getMainBlock();
      if (!block) return null;

      return block.querySelector(
        `${SELECTORS.item}[data-item-index="${itemIndex}"]`
      );
    }

    getAddonInput() {
      return document.querySelector(SELECTORS.addonInput);
    }

    getStickySelect() {
      return document.querySelector(SELECTORS.stickySelect);
    }

    setMainPricesFromVariant(variant) {
      if (!variant) return;

      this.mainPrices = {
        current: variant.price,
        compare: variant.compare_at_price || 0,
      };
    }

    syncMainPricesFromFreshDom() {
      const biscuitsEl = document.querySelector(SELECTORS.mainBiscuitsPrice);
      if (!biscuitsEl?.dataset.biscuitsPrice) return false;

      const current = Number(biscuitsEl.dataset.biscuitsPrice);
      const compare = Number(biscuitsEl.dataset.biscuitsCompareAtPrice || 0);

      this.mainPrices = { current, compare };
      return true;
    }

    getAddonPrices() {
      const addonItem = this.getAddonItem();
      if (!addonItem) return { current: 0, compare: 0 };

      return {
        current: Number(addonItem.dataset.price || 0),
        compare: Number(addonItem.dataset.compareAtPrice || 0),
      };
    }

    setPriceGroup(target, currentCents, compareCents) {
      const currentEl = document.querySelector(target.current);
      const compareEl = document.querySelector(target.compare);
      const currentWrapper = document.querySelector(target.currentWrapper);
      const compareWrapper = document.querySelector(target.compareWrapper);
      const hasCompare = compareCents > currentCents;

      if (currentEl) {
        currentEl.textContent = formatMoney(currentCents);
      }

      if (compareWrapper && compareEl) {
        if (hasCompare) {
          compareEl.textContent = formatMoney(compareCents);
          compareWrapper.hidden = false;
          compareWrapper.style.display = "";
        } else {
          compareWrapper.hidden = true;
          compareWrapper.style.display = "none";
        }
      }

      currentWrapper?.classList.toggle("price--highlight", hasCompare);
    }

    refreshDisplayedPrices() {
      const isAddonActive = this.isAddonActive();
      const { current, compare } = getCombinedPrices(
        this.mainPrices,
        this.getAddonPrices(),
        isAddonActive
      );

      this._isUpdatingPrices = true;

      try {
        PRICE_TARGETS.forEach((target) => {
          this.setPriceGroup(target, current, compare);
        });
      } finally {
        this._isUpdatingPrices = false;
      }
    }

    syncStickySelect(itemIndex, { silent = false } = {}) {
      const select = this.getStickySelect();
      if (!select) return;

      select.value = itemIndex;

      const comboBox = select.closest("combo-box");
      if (comboBox && "selectedValue" in comboBox) {
        comboBox.selectedValue = itemIndex;
      }

      const boundValueId = select.getAttribute("data-bind-value");
      const valueEl = boundValueId ? document.getElementById(boundValueId) : null;
      const selectedOption = select.options[select.selectedIndex];

      if (valueEl && selectedOption) {
        valueEl.textContent =
          selectedOption.getAttribute("title") || selectedOption.textContent;
      }

      if (!silent) {
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    syncStickyToActiveItem({ silent = true } = {}) {
      const activeItem = this.getActiveItem();
      if (!activeItem) return;

      this.syncStickySelect(activeItem.dataset.itemIndex, { silent });
    }

    updateOptionValueLabel(itemLabel) {
      const optionValueId = this.getMainBlock()?.dataset.optionValueId;
      if (!optionValueId || !itemLabel) return;

      const optionValueEl = document.getElementById(optionValueId);
      if (optionValueEl) {
        optionValueEl.textContent = itemLabel;
      }
    }

    updateAddonInput(variantId, isMainProduct) {
      const addonInput = this.getAddonInput();
      if (!addonInput) return;

      addonInput.value = !isMainProduct && variantId ? variantId : "";
    }

    setActiveItem(itemIndex) {
      this.queryItems().forEach((el) => {
        el.classList.toggle("active", el.dataset.itemIndex === itemIndex);
      });
    }

    applySelection(item, { syncSticky = true, updateGallery = true } = {}) {
      const { itemIndex, variantId, itemLabel } = item.dataset;
      const isMainProduct = item.dataset.isMainProduct === "true";

      this.setActiveItem(itemIndex);

      if (syncSticky) {
        this.syncStickySelect(itemIndex);
      }

      this.updateAddonInput(variantId, isMainProduct);
      this.updateOptionValueLabel(itemLabel);

      if (isMainProduct) {
        this.syncMainPricesFromFreshDom();
      }

      this.refreshDisplayedPrices();

      if (updateGallery && item.dataset.slideIndex) {
        this.changeGallerySlide(Number(item.dataset.slideIndex));
      }
    }

    onClick(event) {
      const block = this.getMainBlock();
      if (!block) return;

      const item = event.target.closest(SELECTORS.item);
      if (!item || !block.contains(item)) return;

      event.preventDefault();
      event.stopPropagation();
      this.applySelection(item);
    }

    onStickyChange(event) {
      const select = event.target.closest(SELECTORS.stickySelect);
      if (!select) return;

      const item = this.getItemByIndex(select.value);
      if (!item) return;

      this.applySelection(item, { syncSticky: false });
    }

    syncVariantSpans(variantTitle) {
      this.queryItems().forEach((item) => {
        const spans = item.querySelectorAll(".product-form__variants span");
        const activeSpan = item.querySelector(
          `.product-form__variants span[data-title="${CSS.escape(variantTitle)}"]`
        );

        spans.forEach((span) => span.classList.remove("active"));

        if (!activeSpan) return;

        activeSpan.classList.add("active");

        if (!item.classList.contains("active")) return;

        item.dataset.variantId = activeSpan.dataset.id;

        if (item.dataset.isMainProduct === "false") {
          this.updateAddonInput(activeSpan.dataset.id, false);
        }
      });
    }

    onVariantChanged(event) {
      const variant = event.detail?.variant;
      if (!variant?.title) return;

      this.setMainPricesFromVariant(variant);
      this.syncVariantSpans(variant.title);

      if (this.isAddonActive()) {
        this.schedulePriceRefresh();
      }
    }

    async changeGallerySlide(slideIndex) {
      const gallery = document.querySelector(SELECTORS.gallery);
      if (gallery && typeof gallery.select === "function") {
        await gallery.select(slideIndex);
      }
    }
  }

  window.__customVariantsFormSubmit = async function (formEl, formData) {
    if (!document.querySelector(SELECTORS.block)) return false;

    const addonInput = document.querySelector(SELECTORS.addonInput);
    const mainVariantId = formData.get("id");
    const quantity = Number(formData.get("quantity") || 1);
    const addQueue = [];

    if (mainVariantId) {
      addQueue.push({ id: mainVariantId, quantity, includeProperties: true });
    }

    const addonId = addonInput?.value;
    if (addonId && addonId !== String(mainVariantId)) {
      addQueue.push({ id: addonId, quantity: 1, includeProperties: false });
    }

    if (!addQueue.length) {
      enableFormSubmit(formEl);
      return true;
    }

    try {
      let response;
      let responseJson;

      for (const item of addQueue) {
        response = await mergeOrAddCartItem(
          item.id,
          item.quantity,
          item.includeProperties ? formData : null
        );
        responseJson = await response.json();

        if (!response.ok) {
          enableFormSubmit(formEl);
          console.error("Failed to add to cart:", responseJson.description);
          showCartError(
            formEl,
            responseJson.description ?? "Failed to add items to cart"
          );
          return true;
        }
      }

      await syncCartAttributes(formData);

      enableFormSubmit(formEl);

      const variant = Object.prototype.hasOwnProperty.call(responseJson, "items")
        ? responseJson.items[0]
        : responseJson;

      formEl.dispatchEvent(
        new CustomEvent("Variant:Added", {
          bubbles: true,
          detail: { variant },
        })
      );

      if (typeof window.Cart?.query === "function") {
        const cart = await window.Cart.query();
        const openBasket = !window.themeVariables?.settings?.buyMessage;

        document.documentElement.dispatchEvent(
          new CustomEvent("Cart:Changed", { bubbles: true, detail: { cart } })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent("Cart:Refresh", {
            bubbles: true,
            detail: { openBasket, cart },
          })
        );
      }

      window.dispatchEvent(new CustomEvent("Upsell:Reset"));
      return true;
    } catch (error) {
      console.error("Error during fetch:", error);
      enableFormSubmit(formEl);
      showCartError(formEl, "An unexpected error occurred");
      return true;
    }
  };

  if (!window.__customVariantsBlock) {
    window.__customVariantsBlock = new CustomVariantsBlock();
  }
})();